import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Location from "@/components/Location";
import FAQ from "@/components/FAQ";
import VillaCard from "@/components/VillaCard";
import Activities from "@/components/Activities";
import Booking from "@/components/Booking";
import Testimonials from "@/components/Testimonials";
import PageSEO from "@/components/PageSEO";
import DiscountBanner from "@/components/DiscountBanner";
import SocialIcons from "@/components/SocialIcons";
import { useLang } from "@/contexts/LanguageContext";
import { PROPERTY_LIST } from "@/lib/properties";

const HERO_BG =
  "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?crop=entropy&cs=srgb&fm=jpg&w=2200&q=85";

export default function Home() {
  const { t, lang } = useLang();

  return (
    <div data-testid="home-page" className="min-h-screen bg-[#FAFAF9] text-stone-900">
      <PageSEO
        title="Villas Pura Vida · Hospedaje frente al mar Tuxpan Veracruz · Alquiler de Casa, Departamentos y Lofts Vacacionales de Playa – 3 Alojamientos de Playa frente al Mar en Renta para escapadas de fin de semana desde 2 hasta 26 personas"
        description="Villas Pura Vida en Playa Norte, Barra de Galindo, Tuxpan Veracruz. Alquiler de La Casita (hasta 14 personas), Loft Pura Brisa (6) y Mini Loft Pura Vida (6). Playa semi-virgen, pet-friendly, reserva directo. Nota: no somos Casa Pura Vida; somos Villas Pura Vida — complejo de 3 alojamientos."
        keywords="villas pura vida tuxpan, alquiler casa tuxpan, renta casa frente al mar tuxpan, departamentos vacacionales veracruz, lofts frente al mar tuxpan, casa amplia tuxpan grupos, hospedaje barra de galindo, mini loft tuxpan, escapada fin de semana veracruz, renta vacacional playa, casa familiar tuxpan, mini departamento tipo loft tuxpan"
        path="/"
        image="https://puravidatuxpan.com/api/img/hospedaje-tuxpan-veracruz-barra-galindo-casa-frente-al-mar-casita-01.jpg"
        imageAlt="Villas Pura Vida — hospedaje frente al mar en Playa Norte, Barra de Galindo, Tuxpan Veracruz"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "LodgingBusiness",
          "@id": "https://puravidatuxpan.com/#business",
          name: "Villas Pura Vida",
          alternateName: ["La Casita · Villas Pura Vida", "Villas Pura Vida Tuxpan"],
          description: "Complejo vacacional de 3 alojamientos frente al mar en Playa Norte, Barra de Galindo, Tuxpan, Veracruz: La Casita (hasta 14 huéspedes), Loft Pura Brisa (6) y Mini Loft Pura Vida (6). Hasta 26 personas en total.",
          url: "https://puravidatuxpan.com",
          image: [
            "https://puravidatuxpan.com/api/img/hospedaje-tuxpan-veracruz-barra-galindo-casa-frente-al-mar-casita-01.jpg",
            "https://puravidatuxpan.com/api/img/hospedaje-tuxpan-veracruz-barra-galindo-villa-pura-vida-03.jpg",
          ],
          telephone: "+52 771 129 0073",
          priceRange: "$1,449 – $6,900 MXN",
          address: {
            "@type": "PostalAddress",
            streetAddress: "Playa Norte",
            addressLocality: "Barra de Galindo, Tuxpan",
            addressRegion: "Veracruz",
            postalCode: "92773",
            addressCountry: "MX",
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: 21.0791581,
            longitude: -97.3714559,
          },
          hasMap: "https://www.google.com/maps/place/La+Casita+%E2%80%A2+Villas+Pura+Vida/@21.0791581,-97.3714559,16z",
          numberOfRooms: 3,
          petsAllowed: true,
          starRating: { "@type": "Rating", ratingValue: "5.0" },
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "5.0",
            reviewCount: "29",
            bestRating: "5",
            worstRating: "1",
          },
          amenityFeature: [
            { "@type": "LocationFeatureSpecification", name: "Playa privada frente al mar" },
            { "@type": "LocationFeatureSpecification", name: "Vista al mar y laguna" },
            { "@type": "LocationFeatureSpecification", name: "Pet-friendly" },
            { "@type": "LocationFeatureSpecification", name: "Palapa y asador" },
            { "@type": "LocationFeatureSpecification", name: "Cocina equipada" },
            { "@type": "LocationFeatureSpecification", name: "Aire acondicionado" },
            { "@type": "LocationFeatureSpecification", name: "WiFi de fibra" },
            { "@type": "LocationFeatureSpecification", name: "Estacionamiento privado" },
          ],
          sameAs: [
            "https://www.instagram.com/villaspuravida.tuxpan/",
            "https://www.facebook.com/villaspuravidatuxpan",
            "https://www.tiktok.com/@villa.pura.vida",
            "https://www.airbnb.mx/users/profile/1468263920229106945",
          ],
        }}
      />
      <Header variant="home" />

      {/* Villas Hero */}
      <section id="top" data-testid="villas-hero" className="relative min-h-[100vh] overflow-hidden">
        <div className="absolute inset-0 bg-cover bg-center scale-105" style={{ backgroundImage: `url(${HERO_BG})` }} />
        <div className="absolute inset-0 bg-gradient-to-b from-stone-900/40 via-stone-900/20 to-stone-900/85" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10 pt-40 md:pt-48 pb-24 min-h-[100vh] flex flex-col justify-end">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#E07A5F] text-white text-eyebrow mb-8 fade-up">
              <i className="fa-solid fa-tag text-[10px]" />
              {t("villas_home.hero_badge")}
            </div>
            <p className="text-eyebrow text-stone-200 mb-4 fade-up fade-up-delay-1">
              {t("villas_home.hero_eyebrow")}
            </p>
            <h1
              data-testid="hero-title"
              className="font-serif-display text-white text-5xl sm:text-6xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight mb-6 fade-up fade-up-delay-2"
            >
              {t("villas_home.hero_title_1")}{" "}
              <span className="block italic font-light text-[#F2CC8F]">{t("villas_home.hero_title_2")}</span>
            </h1>
            <p className="text-stone-100 text-lg md:text-xl max-w-2xl mb-10 leading-relaxed fade-up fade-up-delay-3">
              {t("villas_home.hero_sub")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 fade-up fade-up-delay-4">
              <a
                href="#villas"
                data-testid="hero-villas-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#E07A5F] hover:bg-[#D3674B] text-white px-8 py-4 text-sm uppercase tracking-[0.18em] font-semibold transition-all hover:-translate-y-0.5"
              >
                {t("villas_home.hero_cta")}
                <i className="fa-solid fa-arrow-down text-xs" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Rotating discount banner (auto-hides when no active promos) */}
      <DiscountBanner />

      {/* Two villas */}
      <section id="villas" data-testid="villas-section" className="bg-[#FAFAF9] py-24 md:py-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
            <div className="lg:col-span-7">
              <p className="text-eyebrow text-[#E07A5F] mb-4">{t("villas_home.villas_eyebrow")}</p>
              <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900">
                {t("villas_home.villas_title")}
              </h2>
            </div>
            <div className="lg:col-span-5 flex items-end">
              <p className="text-stone-700 text-lg leading-relaxed">{t("villas_home.villas_sub")}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {PROPERTY_LIST.map((p, i) => (
              <VillaCard key={p.id} property={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Story of the complex */}
      <section id="historia" data-testid="story-complex" className="bg-stone-100 py-24 md:py-40">
        <div className="max-w-5xl mx-auto px-6 md:px-10 text-center">
          <p className="text-eyebrow text-[#E07A5F] mb-4">{t("villas_home.story_eyebrow")}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-900 mb-10">
            {t("villas_home.story_title")}
          </h2>
          <p className="text-stone-700 text-lg leading-relaxed mb-6 max-w-3xl mx-auto">
            {t("villas_home.story_p1")}
          </p>
          <p className="font-serif-display italic text-2xl md:text-3xl text-stone-800 max-w-3xl mx-auto leading-snug">
            “{t("villas_home.story_p2")}”
          </p>
        </div>
      </section>

      {/* Social & direct contact — middle of home */}
      <SocialIcons
        variant="section"
        testPrefix="home-mid"
        whatsappMessage={lang === "es" ? "Hola, me interesa reservar en Villas Pura Vida" : "Hi, I want to book Villas Pura Vida"}
      />

      {/* Grandfather's Legacy — brand pillar section */}
      <section
        id="legado"
        data-testid="legado-section"
        className="relative bg-[#1F1A17] text-stone-100 py-24 md:py-40 overflow-hidden"
      >
        {/* Subtle warm texture overlay */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 20%, #E07A5F 0%, transparent 45%), radial-gradient(circle at 80% 75%, #F2CC8F 0%, transparent 55%)",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10">
          <p
            data-testid="legado-eyebrow"
            className="text-eyebrow text-[#F2CC8F] mb-6"
          >
            {t("villas_home.legado_eyebrow")}
          </p>
          <h2
            data-testid="legado-title"
            className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-stone-50 mb-12"
          >
            {t("villas_home.legado_title")}
          </h2>
          <div className="space-y-7 mb-14 max-w-3xl">
            <p
              data-testid="legado-p1"
              className="text-stone-100 text-lg md:text-xl leading-relaxed first-letter:font-serif-display first-letter:text-5xl first-letter:md:text-6xl first-letter:text-[#E07A5F] first-letter:float-left first-letter:mr-3 first-letter:leading-[0.95] first-letter:mt-1"
            >
              {t("villas_home.legado_p1")}
            </p>
            <p data-testid="legado-p2" className="text-stone-200 text-lg leading-relaxed">
              {t("villas_home.legado_p2")}
            </p>
            {/* Narrative pause divider */}
            <div className="flex items-center gap-4 py-2" aria-hidden>
              <span className="h-px w-10 bg-[#E07A5F]/60" />
              <span className="text-[#F2CC8F]/70 text-xs tracking-[0.3em] uppercase">
                {t("villas_home.legado_divider")}
              </span>
              <span className="h-px flex-1 bg-[#E07A5F]/20" />
            </div>
            <p data-testid="legado-p3" className="text-stone-200 text-lg leading-relaxed">
              {t("villas_home.legado_p3")}
            </p>
            <p data-testid="legado-p4" className="text-stone-200 text-lg leading-relaxed">
              {t("villas_home.legado_p4")}
            </p>
          </div>
          <div className="border-l-2 border-[#E07A5F] pl-6 md:pl-8 py-2">
            <p
              data-testid="legado-slogan"
              className="font-serif-display italic text-2xl md:text-3xl lg:text-4xl text-[#F2CC8F] leading-snug"
            >
              {t("villas_home.legado_slogan")}
            </p>
          </div>
        </div>
      </section>

      {/* Shared amenities */}
      <section data-testid="shared-section" className="bg-[#FAFAF9] py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="max-w-3xl mb-12">
            <h3 className="font-serif-display text-3xl md:text-4xl text-stone-900 leading-tight mb-4">
              {t("villas_home.shared_title")}
            </h3>
            <p className="text-stone-700 text-lg leading-relaxed">{t("villas_home.shared_sub")}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-stone-200 border border-stone-200">
            {[
              { i: "fa-solid fa-umbrella-beach", t: "Frente al mar" },
              { i: "fa-solid fa-water", t: "Vista al mar y río" },
              { i: "fa-solid fa-square-parking", t: "Estacionamiento privado" },
              { i: "fa-solid fa-snowflake", t: "Climatizadas" },
              { i: "fa-solid fa-paw", t: "Pet-friendly" },
              { i: "fa-solid fa-utensils", t: "Cocina equipada" },
              { i: "fa-solid fa-wifi", t: "WiFi de fibra" },
              { i: "fa-solid fa-shield-halved", t: "Privada segura" },
            ].map((a, i) => (
              <div key={i} className="bg-stone-50 p-6 md:p-7 text-center hover:bg-white transition-colors">
                <i className={`${a.i} text-2xl text-[#E07A5F] mb-3 block`} />
                <p className="text-sm text-stone-700">{a.t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Location />
      <Activities />
      <Testimonials />
      <Booking allowPicker />
      <FAQ />
      <Footer />
    </div>
  );
}
